import { Award, Trophy, Gift, Plane } from "lucide-react";
import { inr } from "@/lib/api";

/**
 * RankProgressCard — current 14-rank position + pair progress to next rank +
 * child-achiever requirement + Monthly Bonus (2:1) current & next tier.
 *
 * Expects the payload from `GET /api/rewards/me`:
 *   { current_rank, progress, trips_qualified, annual_pairs,
 *     monthly_left_sp, monthly_right_sp, monthly_bonus_tier, next_monthly_bonus_tier }
 */
export default function RankProgressCard({ rewards }) {
  if (!rewards) return null;
  const cur = rewards.current_rank || {};
  const prog = rewards.progress;
  const mbCur = rewards.monthly_bonus_tier;
  const mbNext = rewards.next_monthly_bonus_tier;
  const mLeft = rewards.monthly_left_sp || 0;
  const mRight = rewards.monthly_right_sp || 0;
  const mHigh = Math.max(mLeft, mRight);
  const mMatch = Math.min(mLeft, mRight);

  return (
    <div className="bg-white border border-navy/15 rounded-lg overflow-hidden shadow-sm" data-testid="rank-progress-card">
      <div className="px-5 py-3 border-b-2 border-gold/40 flex items-center gap-2 bg-cream/40">
        <Award size={16} className="text-gold-deep" />
        <div className="font-serif text-lg font-bold text-navy">Rank & Bonus Progress</div>
      </div>

      <div className="p-5 bg-gradient-to-br from-cream to-white">
        <div className="text-xs uppercase tracking-wider text-navy/70 font-bold">Current Rank</div>
        <div className="mt-1 flex items-baseline justify-between gap-2 flex-wrap">
          <div className="font-serif text-2xl font-extrabold text-navy" data-testid="rank-current-label">{cur.label || "Member"}</div>
          <div className="text-xs uppercase tracking-wider text-gold-deep font-bold">
            {(rewards.annual_pairs ?? 0).toLocaleString("en-IN")} pairs / yr
          </div>
        </div>
        {cur.trip && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-olive font-bold px-2 py-0.5 rounded-full bg-olive/10 border border-olive/25">
            <Plane size={12} /> {cur.trip} Trip unlocked
          </div>
        )}
      </div>

      {prog ? (
        <div className="p-5 border-t border-navy/10" data-testid="next-rank">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-navy/70 font-semibold">Next: <span className="text-navy font-bold">{prog.next_rank.label}</span></span>
            <span className="text-gold-deep font-bold tabular-nums">{prog.annual_pairs}/{prog.next_rank.min_pairs}</span>
          </div>
          <div className="mt-2 h-2.5 bg-navy/10 rounded-full overflow-hidden ring-1 ring-navy/10">
            <div style={{ width: `${prog.pairs_pct}%` }} className="h-full bg-gradient-to-r from-gold to-gold-deep transition-all duration-500" data-testid="next-rank-bar" />
          </div>
          {prog.child_need > 0 && (
            <div className="mt-3 text-xs text-navy/75 font-semibold" data-testid="child-req">
              Child achievers: <span className={`font-extrabold ${prog.child_have >= prog.child_need ? "text-olive" : "text-rose"}`}>{prog.child_have}</span> of {prog.child_need} required
            </div>
          )}
        </div>
      ) : (
        <div className="p-5 border-t border-navy/10 text-sm text-navy/70 font-semibold">You've reached the top rank — congratulations!</div>
      )}

      {/* Monthly Bonus (2:1 SP ratio) */}
      <div className="p-5 border-t border-navy/10 bg-navy/[0.03]" data-testid="monthly-bonus-widget">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-navy/80 font-bold mb-2">
          <Gift size={13} className="text-gold-deep" /> Monthly Bonus (2:1)
        </div>
        {mbCur ? (
          <div className="mb-3 text-sm">
            <div className="text-olive font-bold inline-flex items-center gap-1.5">
              <Trophy size={13} /> Tier {mbCur.tier} achieved · {inr(mbCur.bonus)}
            </div>
          </div>
        ) : null}
        {mbNext ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-navy/80 font-semibold">
                Next: <span className="font-bold text-navy">Tier {mbNext.tier}</span>
              </span>
              <span className="text-base font-serif font-extrabold text-gold-deep tabular-nums" data-testid="mb-next-bonus">{inr(mbNext.bonus)}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-navy/70 uppercase tracking-wider font-bold">Higher side</div>
                <div className="text-navy font-extrabold tabular-nums mt-0.5">{mHigh.toLocaleString("en-IN")} / {mbNext.high_sp.toLocaleString("en-IN")}</div>
                <div className="mt-1.5 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                  <div style={{ width: `${Math.min(100, Math.round(mHigh * 100 / mbNext.high_sp))}%` }} className="h-full bg-gold" />
                </div>
              </div>
              <div>
                <div className="text-navy/70 uppercase tracking-wider font-bold">Matching side</div>
                <div className="text-navy font-extrabold tabular-nums mt-0.5">{mMatch.toLocaleString("en-IN")} / {mbNext.match_sp.toLocaleString("en-IN")}</div>
                <div className="mt-1.5 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                  <div style={{ width: `${Math.min(100, Math.round(mMatch * 100 / mbNext.match_sp))}%` }} className="h-full bg-olive" />
                </div>
              </div>
            </div>
          </>
        ) : mbCur ? (
          <div className="text-sm text-navy/70 font-medium">All 13 levels hit this month — legend status.</div>
        ) : (
          <div className="text-sm text-navy/70 font-medium">Build 200 higher-side SP and 100 matching-side SP this month for Level 1 (₹1,500).</div>
        )}
      </div>
    </div>
  );
}
